import React, { useState } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Avatar,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
  alpha,
  useTheme,
  Rating
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  MoreVert as MoreIcon,
  CheckCircle as VerifiedIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon,
  LocationOn as LocationIcon,
  CleaningServices as CleaningIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";

const mockCleaners = [
  {
    id: 1,
    name: "Maria Garcia",
    email: "maria.g@example.com",
    phone: "+1 (555) 123-4567",
    status: "Available",
    verification: "Verified",
    joinDate: "2024-01-15",
    rating: 4.9,
    totalJobs: 156,
    specialty: "Deep Cleaning",
    location: "New York, NY",
    hourlyRate: 35,
    avatar: "",
    nextAvailable: "2024-03-23"
  },
  {
    id: 2,
    name: "James Wilson",
    email: "james.w@example.com",
    phone: "+1 (555) 987-6543",
    status: "Working",
    verification: "Verified",
    joinDate: "2024-02-10",
    rating: 4.8,
    totalJobs: 89,
    specialty: "Standard Cleaning",
    location: "Los Angeles, CA",
    hourlyRate: 30,
    avatar: "",
    nextAvailable: "2024-03-24"
  }
];

export default function Cleaners() {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");

  const filteredCleaners = mockCleaners.filter(cleaner =>
    cleaner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cleaner.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = [
    { label: 'Total Cleaners', value: '156', change: '+8%' },
    { label: 'Available Now', value: '89', change: '+12%' },
    { label: 'Avg. Rating', value: '4.8/5', change: '+0.1' },
    { label: 'Active Jobs', value: '45', change: '+5%' },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            Cleaners
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
          >
            Add Cleaner
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage cleaner profiles, availability, and performance
        </Typography>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {stat.label}
                </Typography>
                <Chip 
                  label={stat.change} 
                  size="small" 
                  color="success"
                  variant="outlined"
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search cleaners..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <Button variant="outlined" startIcon={<FilterIcon />}>
              Filters
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Cleaners Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
              <TableRow>
                <TableCell>Cleaner</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Specialty</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Verification</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Total Jobs</TableCell>
                <TableCell>Hourly Rate</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCleaners.map((cleaner) => (
                <TableRow key={cleaner.id} hover sx={{ cursor: 'pointer' }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={<CleaningIcon sx={{ fontSize: 16 }} />}
                      >
                        <Avatar src={cleaner.avatar}>
                          {cleaner.name.charAt(0)}
                        </Avatar>
                      </Badge>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="600">
                          {cleaner.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Since {new Date(cleaner.joinDate).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{cleaner.email}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {cleaner.phone}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={cleaner.specialty} 
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={cleaner.status}
                      size="small"
                      color={cleaner.status === 'Available' ? 'success' : 'warning'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={cleaner.verification}
                      size="small"
                      color={cleaner.verification === 'Verified' ? 'success' : 'warning'}
                      icon={cleaner.verification === 'Verified' ? <VerifiedIcon /> : <ScheduleIcon />}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Rating value={cleaner.rating} precision={0.1} size="small" readOnly />
                      <Typography variant="body2" fontWeight="600">
                        {cleaner.rating}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" fontWeight="600">
                      {cleaner.totalJobs}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" fontWeight="600" color="primary">
                      ${cleaner.hourlyRate}/hr
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small">
                      <MoreIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}